import { supabase } from '../lib/supabase';

class SMSNotificationService {
  /**
   * Send SMS notification via Twilio Edge Function
   * @param {string} phoneNumber - Customer phone number
   * @param {string} message - SMS message content
   * @param {object} appointmentData - Appointment details
   * @param {string} notificationType - Type of notification
   */
  async sendSMS(phoneNumber, message, appointmentData = {}, notificationType = 'general') {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-sms-notification', {
        body: {
          to: phoneNumber,
          message: message,
          appointmentData: appointmentData,
          notificationType: notificationType
        }
      });

      if (error) {
        console.error('SMS sending error:', error);
        throw error;
      }

      console.log('SMS sent successfully:', data);
      return data;
    } catch (error) {
      console.error('Failed to send SMS:', error);
      throw error;
    }
  }

  /**
   * Send booking confirmation SMS
   */
  async sendBookingConfirmation(appointmentData) {
    const message = `PlumberPro: Votre rendez-vous est confirmé! Réf: ${appointmentData?.reference}. Date: ${appointmentData?.scheduledDate} à ${appointmentData?.scheduledTimeStart}. Adresse: ${appointmentData?.customerAddress}. Merci!`;
    
    return this.sendSMS(
      appointmentData?.customerPhone,
      message,
      appointmentData,
      'booking_confirmed'
    );
  }

  /**
   * Send technician en route SMS
   */
  async sendTechnicianEnRoute(appointmentData) {
    const technicianName = appointmentData?.technicianName || 'Votre technicien';
    const eta = appointmentData?.estimatedArrivalTime || 'bientôt';
    
    const message = `PlumberPro: ${technicianName} est en route vers votre domicile. Arrivée estimée: ${eta}. Réf: ${appointmentData?.reference}. Suivez en temps réel sur votre espace client.`;
    
    return this.sendSMS(
      appointmentData?.customerPhone,
      message,
      appointmentData,
      'technician_en_route'
    );
  }

  /**
   * Send service completion SMS
   */
  async sendServiceCompletion(appointmentData) {
    const message = `PlumberPro: L'intervention est terminée. Réf: ${appointmentData?.reference}. Merci de votre confiance! Un récapitulatif détaillé vous a été envoyé par email. N'hésitez pas à nous laisser un avis.`;
    
    return this.sendSMS(
      appointmentData?.customerPhone,
      message,
      appointmentData,
      'service_completed'
    );
  }

  /**
   * Send proximity alert SMS (15, 10, 5 minutes)
   */
  async sendProximityAlert(appointmentData, minutesAway) {
    const message = `PlumberPro: Votre technicien arrive dans ${minutesAway} minutes! Réf: ${appointmentData?.reference}. Préparez-vous à l'accueillir.`;
    
    return this.sendSMS(
      appointmentData?.customerPhone,
      message,
      appointmentData,
      `proximity_${minutesAway}min`
    );
  }

  /**
   * Send arrival notification SMS
   */
  async sendArrivalNotification(appointmentData) {
    const message = `PlumberPro: Votre technicien est arrivé à votre domicile. Réf: ${appointmentData?.reference}. L'intervention va commencer.`;
    
    return this.sendSMS(
      appointmentData?.customerPhone,
      message,
      appointmentData,
      'arrival'
    );
  }

  /**
   * Check if customer has SMS enabled in preferences
   */
  async isSMSEnabled(customerEmail) {
    try {
      const { data, error } = await supabase?.from('notification_preferences')?.select('enable_sms')?.eq('customer_email', customerEmail)?.single();

      if (error || !data) {
        // Default to false if no preferences found
        return false;
      }

      return data?.enable_sms === true;
    } catch (error) {
      console.error('Error checking SMS preferences:', error);
      return false;
    }
  }

  /**
   * Send notification with preference check
   */
  async sendWithPreferenceCheck(appointmentData, notificationType) {
    try {
      // Check if SMS is enabled for this customer
      const smsEnabled = await this.isSMSEnabled(appointmentData?.customerEmail);
      
      if (!smsEnabled) {
        console.log('SMS notifications disabled for customer:', appointmentData?.customerEmail);
        return { skipped: true, reason: 'SMS disabled in preferences' };
      }

      // Send appropriate SMS based on notification type
      switch (notificationType) {
        case 'booking_confirmed':
          return await this.sendBookingConfirmation(appointmentData);
        case 'technician_en_route':
          return await this.sendTechnicianEnRoute(appointmentData);
        case 'service_completed':
          return await this.sendServiceCompletion(appointmentData);
        case 'arrival':
          return await this.sendArrivalNotification(appointmentData);
        default:
          console.warn('Unknown notification type:', notificationType);
          return { skipped: true, reason: 'Unknown notification type' };
      }
    } catch (error) {
      console.error('Error sending SMS with preference check:', error);
      throw error;
    }
  }
}

export const smsNotificationService = new SMSNotificationService();
export default smsNotificationService;